import React, { createContext, useContext, useEffect, useState } from "react";
import { has } from "@kinde-oss/kinde-auth-react/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

const AccessControlContext = createContext();

export function AccessControlProvider({ children }) {
  const { isAuthenticated } = useKindeAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      if (isAuthenticated) {
        try {
          const adminResult = await has({ roles: ["admin"] });
          setIsAdmin(adminResult);
        } catch (error) {
          console.error("Error checking admin role:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setIsLoading(false);
    };

    checkAdmin();
  }, [isAuthenticated]);

  /**
   * Check if user has a specific role or any of the roles in an array
   * @param {string|string[]} roles - Single role or array of roles to check
   * @returns {Promise<boolean>} True if user has at least one of the specified roles
   */
  const hasRole = async (roles) => {
    try {
      const rolesArray = Array.isArray(roles) ? roles : [roles];
      const result = await has({ roles: rolesArray });
      return result;
    } catch (error) {
      console.error("Error checking role:", error);
      return false;
    }
  };

  return (
    <AccessControlContext.Provider value={{ isAdmin, isLoading, hasRole }}>
      {children}
    </AccessControlContext.Provider>
  );
}

export function useAccessControl() {
  const context = useContext(AccessControlContext);

  // The public organisation tree sometimes mounts without the provider (on
  // custom domains there is no Kinde session). A benign default keeps those
  // pages read-only instead of crashing.
  if (!context) {
    return { isAdmin: false, isLoading: false };
  }

  return context;
}
